import { allOf, anyOf, formResult, signalEquals, systemEquals, } from './equation.js';
import { RUNTIME_EVENTS, SCRATCHPAD_EVENTS } from './graph.js';
import { startConversationOpening } from './template-openings.js';
/**
 * The inbound receptionist: greet, find out why they called, then route to
 * leasing, maintenance, tenancy, or vendor help. Maintenance screens for an
 * emergency and a 911 issue. Anything the agent cannot handle goes to a node
 * that takes a message for the team.
 *
 * The playground cannot transfer a call. Each place the live agent transfers
 * is a say node that tells the caller so, then goes on to take a message.
 *
 * The knowledge base ids are the playground's sample ids (see
 * sample-knowledge-bases in the playground app).
 */
const START_ID = '00000000-0000-4000-8000-00000009d001';
const GREET_ID = '00000000-0000-4000-8000-00000009a001';
const INTENT_ID = '00000000-0000-4000-8000-00000009c001';
const LEASING_ID = '00000000-0000-4000-8000-00000009c002';
const MAINTENANCE_FORM_ID = '00000000-0000-4000-8000-00000009f001';
const URGENCY_ID = '00000000-0000-4000-8000-00000009c008';
const MAINTENANCE_ID = '00000000-0000-4000-8000-00000009c003';
const TENANCY_ID = '00000000-0000-4000-8000-00000009c004';
const VENDOR_ID = '00000000-0000-4000-8000-00000009c005';
const ANYTHING_ELSE_ID = '00000000-0000-4000-8000-00000009c006';
const MESSAGE_ID = '00000000-0000-4000-8000-00000009c007';
const TRANSFER_AFTER_HOURS_ID = '00000000-0000-4000-8000-00000009b001';
const TRANSFER_BUSINESS_HOURS_ID = '00000000-0000-4000-8000-00000009b002';
const TRANSFER_VENDOR_ID = '00000000-0000-4000-8000-00000009b003';
const END_CLOSE_ID = '00000000-0000-4000-8000-00000009a002';
const END_911_ID = '00000000-0000-4000-8000-00000009a003';
const edgeId = (n) => `00000000-0000-4000-8000-00000009e${String(n).padStart(3, '0')}`;
const OPENING = startConversationOpening({
    startNodeId: START_ID,
    entryEdgeId: edgeId(1),
    exitEdgeId: edgeId(27),
    toNodeId: GREET_ID,
});
const LEASING_KB = 'sample-leasing';
const MAINTENANCE_KB = 'sample-pest-control';
const event = (name) => ({ kind: 'event', event: name });
/** What a transfer say node tells the caller. */
const transferText = (team) => `In your live agent, this is where I'd transfer you to ${team}. For ` +
    "this demo, I'll take a message instead.";
export const RECEPTIONIST_GRAPH_TEMPLATE = {
    name: 'Receptionist',
    persona: 'You are Ziggy, a friendly assistant for Super rental management. One ' +
        'or two short spoken sentences per turn.',
    voiceSettings: { voice: 'en-US-Chirp3-HD-Erinome' },
    active: true,
    knowledgeBaseIds: [],
    watchEvents: [SCRATCHPAD_EVENTS.HANDOFF_REQUESTED],
    nodes: [
        OPENING.node,
        {
            id: GREET_ID,
            type: 'say',
            title: 'Greeting',
            position: 1,
            text: 'Hello, this is Ziggy with Super Management. How can I help you ' +
                'today?',
        },
        {
            id: MESSAGE_ID,
            type: 'conversation',
            title: 'Take a message',
            position: 2,
            prompt: "Say that you'll log a message for the team with details to follow " +
                'up on the inquiry as soon as possible. Gather information like ' +
                'their name and relevant details for follow up (e.g. property ' +
                'address if tenant, company name if vendor, best phone number for ' +
                'callback). If they insist to speak to a person, say that ' +
                'unfortunately management is not currently available to take the ' +
                'call, but you will make sure this message gets in front of them. ' +
                'Take down their message and read it back briefly. Then ask: "Is ' +
                'there anything else I can help with?"',
            signals: [
                {
                    id: 'message_taken',
                    text: 'The caller gave their message for the team and said there is ' +
                        'nothing else',
                },
            ],
            tools: [],
            knowledgeBaseIds: [],
            excludedKnowledgeBaseIds: [],
        },
        {
            id: END_CLOSE_ID,
            type: 'end_conversation',
            title: 'End conversation',
            position: 3,
            signOff: {
                mode: 'prompt',
                promptText: 'Sign off naturally and say goodbye.',
                exactText: 'Thank you for calling Super Management. Have a good day.',
            },
        },
        {
            id: INTENT_ID,
            type: 'conversation',
            title: 'Intent',
            position: 4,
            prompt: 'First ask:\nWhat can I help you with?\n\nOnly if there is any ' +
                'ambiguity for maintenance on whether this is a vendor or a tenant, ' +
                "ask. If it's clear they're a tenant or a vendor, then move ahead.",
            signals: [
                {
                    id: 'leasing_inquiry',
                    text: 'Is the caller looking to speak about leasing (e.g. rental ' +
                        'inquiry, renting a property, listing info, their application ' +
                        'status, how to apply, information about a property)',
                },
                {
                    id: 'maintenance_issue',
                    text: 'Is the caller a tenant or owner looking to speak about a ' +
                        'maintenance issue or emergency (e.g. repair request, something ' +
                        'broken, work order, status of repair, emergency issue)',
                },
                {
                    id: 'tenancy_inquiry',
                    text: 'Is the caller looking to speak about their tenancy (e.g. lease ' +
                        'status, lease update, move-in, move-out, security deposit, ' +
                        'changing lease, payment issue, fee dispute, rent payment, ' +
                        'general complaint)',
                },
                {
                    id: 'vendor',
                    text: 'Is the caller a vendor, service-oriented professional like ' +
                        'plumber, technician, etc, or asking for information about a job ' +
                        "they're working / asking for task details related to their " +
                        "client-facing work, or cost approvals for work or job they're " +
                        'performing, or on-site at the property for a job.',
                },
            ],
            tools: [],
            knowledgeBaseIds: [],
            excludedKnowledgeBaseIds: [],
        },
        {
            id: LEASING_ID,
            type: 'conversation',
            title: 'Leasing',
            position: 5,
            prompt: 'Help them answer questions about our available rental properties, ' +
                'applications, and leasing FAQs.\n\nListen for their questions ' +
                'first. If they do not have any other questions and have found a ' +
                'property they are interested in, then help them book a showing.',
            speaksOnEntry: true,
            tools: [
                { tool: 'GET_PROPERTY_LISTINGS' },
                { tool: 'TENANT_SHOWING_SCHEDULING' },
            ],
            knowledgeBaseIds: [LEASING_KB],
            excludedKnowledgeBaseIds: [],
        },
        {
            id: MAINTENANCE_FORM_ID,
            type: 'form',
            title: 'Maintenance',
            position: 6,
            items: [
                {
                    kind: 'question',
                    id: 'property',
                    text: 'Can you confirm your property address?',
                    mode: 'verbatim',
                    depth: 0,
                    answerType: { kind: 'free_text' },
                    required: false,
                    scratchpadMapping: {
                        path: 'properties.resolved.address',
                        direction: 'read_write',
                        renderPolicy: 'skip_if_present',
                    },
                },
            ],
            tools: [],
            knowledgeBaseIds: [MAINTENANCE_KB],
            excludedKnowledgeBaseIds: [],
        },
        {
            id: TRANSFER_AFTER_HOURS_ID,
            type: 'say',
            title: 'Transfer',
            position: 7,
            text: transferText('the on-call team'),
        },
        {
            id: MAINTENANCE_ID,
            type: 'conversation',
            title: 'Maintenance',
            position: 8,
            prompt: 'Attempt basic troubleshooting based on the issue communicated.\n\n' +
                'Once troubleshooting has been done (or confirmed to already been ' +
                "attempted), then let the caller know that you'll create a work " +
                'order in the system for them and notify the team about the ' +
                'request. Then ask if you can help with anything else?',
            signals: [
                { id: 'troubleshooting', text: 'Did we offer to create a work order?' },
                {
                    id: 'additional_help_needed',
                    text: 'Do they need any additional help?',
                },
            ],
            speaksOnEntry: true,
            tools: [],
            knowledgeBaseIds: [MAINTENANCE_KB],
            excludedKnowledgeBaseIds: [],
        },
        {
            id: ANYTHING_ELSE_ID,
            type: 'conversation',
            title: 'Anything else?',
            position: 9,
            prompt: 'Ask if there is anything else you can help the caller with today.',
            signals: [{ id: 'help', text: 'Do they need any more help?' }],
            tools: [],
            knowledgeBaseIds: [],
            excludedKnowledgeBaseIds: [],
        },
        {
            id: TENANCY_ID,
            type: 'conversation',
            title: 'Tenancy',
            position: 10,
            prompt: 'Help answer their questions from your knowledge. If there is a ' +
                'question or request outside of your knowledge, let them know that ' +
                "you'll log this request for the team to follow up as soon as " +
                'possible.',
            signals: [
                {
                    id: 'answered',
                    text: 'Were all questions answered or was a promise to log a message ' +
                        'for the team given?',
                },
            ],
            speaksOnEntry: true,
            tools: [],
            knowledgeBaseIds: [],
            excludedKnowledgeBaseIds: [],
        },
        {
            id: VENDOR_ID,
            type: 'conversation',
            title: 'Vendor',
            position: 11,
            prompt: 'First identify if the caller is a vendor that we are currently ' +
                'engaged with on a project or property. If they are working on a ' +
                'work order, on-site at the property, need an approval, or need to ' +
                'discuss the active job, or if they are not a current vendor (such ' +
                'as soliciting business or not actively working on a job).',
            signals: [
                {
                    id: 'transfer',
                    text: 'Is this a current vendor working on an active job?',
                },
            ],
            speaksOnEntry: true,
            tools: [],
            knowledgeBaseIds: [],
            excludedKnowledgeBaseIds: [],
        },
        {
            id: TRANSFER_VENDOR_ID,
            type: 'say',
            title: 'Transfer',
            position: 12,
            text: transferText('the team'),
        },
        {
            id: TRANSFER_BUSINESS_HOURS_ID,
            type: 'say',
            title: 'Transfer',
            position: 13,
            text: transferText('the maintenance team'),
        },
        {
            id: END_911_ID,
            type: 'end_conversation',
            title: 'End',
            position: 14,
            signOff: {
                mode: 'exact',
                promptText: '',
                exactText: 'I will notify the team, but please get to safety and call 911 ' +
                    'right away. This call will be ending so you can call 911 ' +
                    'services.',
            },
        },
        {
            id: URGENCY_ID,
            type: 'conversation',
            title: 'Determine urgency',
            position: 15,
            prompt: "Ask for a description of the issue. You need this to determine if it's an emergency, regular maintenance, and the type of maintenance. Try to get this if not already provided or obvious:\n\n" +
                '- Where the issue is\n- When it started\n- Details about what is happening\n- Severity\n- What troubleshooting (if any) has been done\n\n' +
                'If any of the following criteria are met, then this is an emergency:\n' +
                '- Unstoppable leaking/flooding/burst pipe\n- Sewage backup or smell\n- No functional HVAC for heating or cooling\n\n' +
                'When it is an emergency, state that this sounds like an emergency.  Ask if there is anything else you should be aware of before I connect you?\n\n' +
                'When it is regular maintenance, state that this sounds like a maintenance issue.\n\n' +
                '911 issues are:\n- Fire or visible smoke\n- Active crime in progress (e.g. break in, violence)\n- Gas smell or leak\n\n' +
                'If the issue is 911-related, then advise the customer to get to safety and call 911 right away. You will notify the team but in the meantime, they need to go directly to emergency services. Ask if there is anything else you should be aware of before we let you go?',
            signals: [
                { id: 'is_911', text: 'Is this a 911 issue?' },
                { id: 'is_emergency', text: 'Is this an emergency?' },
            ],
            speaksOnEntry: true,
            tools: [],
            knowledgeBaseIds: [MAINTENANCE_KB],
            excludedKnowledgeBaseIds: [],
        },
    ],
    edges: [
        ...OPENING.edges,
        {
            id: edgeId(2),
            fromNodeId: MESSAGE_ID,
            toNodeId: END_CLOSE_ID,
            condition: allOf(signalEquals(MESSAGE_ID, 'message_taken', true)),
            position: 2,
        },
        {
            id: edgeId(3),
            fromNodeId: GREET_ID,
            toNodeId: INTENT_ID,
            condition: { type: 'event', event: RUNTIME_EVENTS.TEXT_DELIVERED },
            position: 3,
        },
        {
            id: edgeId(4),
            fromNodeId: INTENT_ID,
            toNodeId: LEASING_ID,
            condition: allOf(signalEquals(INTENT_ID, 'leasing_inquiry', true)),
            position: 4,
        },
        {
            id: edgeId(5),
            fromNodeId: INTENT_ID,
            toNodeId: MESSAGE_ID,
            condition: allOf(event(SCRATCHPAD_EVENTS.HANDOFF_REQUESTED), signalEquals(INTENT_ID, 'maintenance_issue', false), signalEquals(INTENT_ID, 'leasing_inquiry', false), signalEquals(INTENT_ID, 'vendor', false)),
            position: 5,
        },
        {
            id: edgeId(6),
            fromNodeId: LEASING_ID,
            toNodeId: MESSAGE_ID,
            condition: { type: 'event', event: SCRATCHPAD_EVENTS.HANDOFF_REQUESTED },
            position: 6,
        },
        {
            id: edgeId(7),
            fromNodeId: INTENT_ID,
            toNodeId: MAINTENANCE_FORM_ID,
            condition: allOf(signalEquals(INTENT_ID, 'maintenance_issue', true)),
            position: 7,
        },
        {
            id: edgeId(8),
            fromNodeId: MAINTENANCE_FORM_ID,
            toNodeId: URGENCY_ID,
            condition: allOf(formResult(MAINTENANCE_FORM_ID, 'completed')),
            position: 8,
        },
        {
            id: edgeId(9),
            fromNodeId: ANYTHING_ELSE_ID,
            toNodeId: MESSAGE_ID,
            condition: allOf(signalEquals(ANYTHING_ELSE_ID, 'help', true), event(SCRATCHPAD_EVENTS.HANDOFF_REQUESTED)),
            position: 9,
        },
        {
            id: edgeId(10),
            fromNodeId: LEASING_ID,
            toNodeId: ANYTHING_ELSE_ID,
            condition: { type: 'event', event: 'tour_scheduled' },
            position: 10,
        },
        {
            id: edgeId(11),
            fromNodeId: INTENT_ID,
            toNodeId: TENANCY_ID,
            condition: allOf(signalEquals(INTENT_ID, 'tenancy_inquiry', true)),
            position: 11,
        },
        {
            id: edgeId(12),
            fromNodeId: TENANCY_ID,
            toNodeId: ANYTHING_ELSE_ID,
            condition: allOf(signalEquals(TENANCY_ID, 'answered', true)),
            position: 12,
        },
        {
            id: edgeId(13),
            fromNodeId: TENANCY_ID,
            toNodeId: MESSAGE_ID,
            condition: { type: 'event', event: SCRATCHPAD_EVENTS.HANDOFF_REQUESTED },
            position: 13,
        },
        {
            id: edgeId(14),
            fromNodeId: INTENT_ID,
            toNodeId: VENDOR_ID,
            condition: allOf(signalEquals(INTENT_ID, 'vendor', true)),
            position: 14,
        },
        {
            id: edgeId(15),
            fromNodeId: VENDOR_ID,
            toNodeId: TRANSFER_VENDOR_ID,
            condition: allOf(signalEquals(VENDOR_ID, 'transfer', true)),
            position: 15,
        },
        {
            id: edgeId(16),
            fromNodeId: VENDOR_ID,
            toNodeId: MESSAGE_ID,
            condition: anyOf(event(SCRATCHPAD_EVENTS.HANDOFF_REQUESTED), signalEquals(VENDOR_ID, 'transfer', false)),
            position: 16,
        },
        {
            id: edgeId(17),
            fromNodeId: ANYTHING_ELSE_ID,
            toNodeId: END_CLOSE_ID,
            condition: allOf(signalEquals(ANYTHING_ELSE_ID, 'help', false)),
            position: 17,
        },
        {
            id: edgeId(18),
            fromNodeId: TRANSFER_AFTER_HOURS_ID,
            toNodeId: MESSAGE_ID,
            condition: { type: 'event', event: RUNTIME_EVENTS.TEXT_DELIVERED },
            position: 18,
        },
        {
            id: edgeId(19),
            fromNodeId: TRANSFER_VENDOR_ID,
            toNodeId: MESSAGE_ID,
            condition: { type: 'event', event: RUNTIME_EVENTS.TEXT_DELIVERED },
            position: 19,
        },
        {
            id: edgeId(20),
            fromNodeId: TRANSFER_BUSINESS_HOURS_ID,
            toNodeId: MESSAGE_ID,
            condition: { type: 'event', event: RUNTIME_EVENTS.TEXT_DELIVERED },
            position: 20,
        },
        {
            id: edgeId(23),
            fromNodeId: URGENCY_ID,
            toNodeId: END_911_ID,
            condition: allOf(signalEquals(URGENCY_ID, 'is_911', true)),
            position: 21,
        },
        {
            id: edgeId(21),
            fromNodeId: URGENCY_ID,
            toNodeId: TRANSFER_BUSINESS_HOURS_ID,
            condition: allOf(signalEquals(URGENCY_ID, 'is_emergency', true), systemEquals('is_business_hours', true)),
            position: 22,
        },
        {
            id: edgeId(22),
            fromNodeId: URGENCY_ID,
            toNodeId: TRANSFER_AFTER_HOURS_ID,
            condition: allOf(signalEquals(URGENCY_ID, 'is_emergency', true), systemEquals('is_business_hours', false)),
            position: 23,
        },
        {
            id: edgeId(24),
            fromNodeId: MAINTENANCE_ID,
            toNodeId: END_CLOSE_ID,
            condition: allOf(signalEquals(MAINTENANCE_ID, 'troubleshooting', true), signalEquals(MAINTENANCE_ID, 'additional_help_needed', false)),
            position: 24,
        },
        {
            id: edgeId(25),
            fromNodeId: MAINTENANCE_ID,
            toNodeId: MESSAGE_ID,
            condition: allOf(signalEquals(MAINTENANCE_ID, 'troubleshooting', true), signalEquals(MAINTENANCE_ID, 'additional_help_needed', true)),
            position: 25,
        },
        {
            id: edgeId(26),
            fromNodeId: URGENCY_ID,
            toNodeId: MAINTENANCE_ID,
            condition: anyOf(signalEquals(URGENCY_ID, 'is_emergency', false), signalEquals(URGENCY_ID, 'is_911', false)),
            position: 26,
        },
    ],
};
//# sourceMappingURL=receptionist-template.js.map